import './styling/base.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import Navbar from './components/Navbar';
import asignIconwhite from './assets/logos/asign-icon-white.svg';
import playIcon from './assets/icons/play-solid.svg';
import caretLeftIcon from './assets/icons/caret-left-solid.svg';
import caretRightIcon from './assets/icons/caret-right-solid.svg';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Overview from './pages/Overview';
import Forms from './pages/Forms';
import Form1 from './pages/Form1';
import Form2 from './pages/Form2';
import { useTranslation } from 'react-i18next';

/* ── Video sidebar drag-to-resize tuning ─────────────────────────────────────
   Adjust how far the desktop video panel can be dragged. These are the only
   knobs — the values flow into the shared --video-sidebar-width CSS variable,
   which drives the panel plus the matching push on the page content.
   ─────────────────────────────────────────────────────────────────────────── */
const VIDEO_SIDEBAR_DEFAULT_WIDTH = 365; // px — width on load / when opened
const VIDEO_SIDEBAR_MIN_WIDTH = 320;     // px — narrowest (keeps the 4:5 video + controls usable)
const VIDEO_SIDEBAR_MAX_VW = 0.50;       // fraction of the window width — widest (currently 50%)
const VIDEO_SIDEBAR_KEYBOARD_STEP = 24;  // px moved per arrow-key press
const VIDEO_SIDEBAR_NAV_OFFSET = 200;    // px the left nav sidebar adds when opened (300px open − 100px collapsed rail); reclaimed from the video panel when the nav is open so the page content keeps its width

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
// Largest allowed base width in px, derived from the current viewport.
const getVideoSidebarMaxWidth = () =>
  Math.round(window.innerWidth * VIDEO_SIDEBAR_MAX_VW);
// Visible (effective) video width — mirrors the CSS --video-sidebar-effective-width:
// the base width minus the nav's footprint when the nav is open, floored at the min.
const getEffectiveVideoWidth = (baseWidth, navOpen) =>
  Math.max(VIDEO_SIDEBAR_MIN_WIDTH, baseWidth - (navOpen ? VIDEO_SIDEBAR_NAV_OFFSET : 0));
// Largest allowed *visible* width for the current nav state.
const getEffectiveVideoMaxWidth = (navOpen) =>
  Math.max(VIDEO_SIDEBAR_MIN_WIDTH, getVideoSidebarMaxWidth() - (navOpen ? VIDEO_SIDEBAR_NAV_OFFSET : 0));

function LangWrapper({ lang, children }) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang, i18n]);
  return <>{children}</>;
}

const EN_TO_FR = {
  '/': '/fr',
  '/forms': '/fr/formulaires',
  '/forms/disability-tax-credit': '/fr/formulaires/credit-impot-pour-personnes-handicapees',
  '/forms/canada-disability-benefit': '/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees',
};
const FR_TO_EN = {
  '/fr': '/',
  '/fr/formulaires': '/forms',
  '/fr/formulaires/credit-impot-pour-personnes-handicapees': '/forms/disability-tax-credit',
  '/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees': '/forms/canada-disability-benefit',
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [videoSidebarOpen, setVideoSidebarOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [videoLabels, setVideoLabels] = useState([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [allVideoUrls, setAllVideoUrls] = useState([]);
  const [registeredSections, setRegisteredSections] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const sidebarRef = useRef(null);
  const swipeRef = useRef({ startY: 0, currentY: 0, active: false });
  const tabClickSuppressRef = useRef(false);
  // Desktop video-sidebar width (px). Session-only: resets to the default on
  // every page load — not persisted.
  const [videoSidebarWidth, setVideoSidebarWidth] = useState(VIDEO_SIDEBAR_DEFAULT_WIDTH);
  const resizeRef = useRef({ startX: 0, startWidth: 0, active: false });
  const { i18n, t } = useTranslation();

  // When language changes while sidebar is open, immediately switch to the correct tab
  useEffect(() => {
    if (!videoSidebarOpen || allVideoUrls.length === 0) return;
    const defaultIdx = i18n.language === 'fr' ? 1 : 0;
    setSelectedVideoIndex(defaultIdx);
    setCurrentVideoUrl(allVideoUrls[defaultIdx]);
    setIsPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Lock body scroll on mobile while a sidebar is open. Pinning body to
  // position: fixed at -scrollY prevents the page underneath from scrolling
  // and lets us restore the scroll position on close (overflow: hidden on
  // html/body resets it to the top on iOS, which is why we don't use that).
  //
  // useLayoutEffect (not useEffect) + a forced synchronous reflow (void
  // body.offsetHeight) before the scrollTo: this is the combination that
  // stops the brief "snap to top, then scroll back" flash on iOS Safari.
  // The reflow forces the document height to be recomputed against the
  // newly-unfixed body before scrollTo runs, so the scroll lands in the
  // same pre-paint frame as the unlock.
  useLayoutEffect(() => {
    const anyOpen = sidebarOpen || videoSidebarOpen;
    if (!anyOpen || window.innerWidth > 1089) return undefined;

    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      // Force a synchronous layout flush so iOS knows the document's real
      // scroll height before we ask it to scroll. Without this, iOS can
      // composite the unfixed-body frame at scrollTop=0 before applying
      // our scrollTo on the next frame.
      // eslint-disable-next-line no-unused-expressions
      body.offsetHeight;
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [sidebarOpen, videoSidebarOpen]);

  // Push the current width into the shared CSS variable that drives both the
  // panel and the matching page-content offset. Runs on mount (writing the
  // default) and on every drag / keyboard change.
  useEffect(() => {
    document.documentElement.style.setProperty('--video-sidebar-width', `${videoSidebarWidth}px`);
  }, [videoSidebarWidth]);

  // Reflect the nav sidebar's footprint into the CSS. When the nav is open the
  // video panel gives this space back (floored at its min) so the page content
  // keeps its width instead of being squeezed from both sides.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--video-sidebar-nav-offset',
      sidebarOpen ? `${VIDEO_SIDEBAR_NAV_OFFSET}px` : '0px'
    );
  }, [sidebarOpen]);

  // If the viewport shrinks, re-clamp so a panel that was dragged wide can't
  // end up larger than the current max (75% of the new width).
  useEffect(() => {
    const onResize = () => {
      setVideoSidebarWidth(w => clamp(w, VIDEO_SIDEBAR_MIN_WIDTH, getVideoSidebarMaxWidth()));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const registerSection = useCallback((data) => {
    setRegisteredSections(prev => {
      if (prev.find(s => s.sectionId === data.sectionId)) return prev;
      return [...prev, data];
    });
  }, []);

  const unregisterSection = useCallback((sectionId) => {
    setRegisteredSections(prev => prev.filter(s => s.sectionId !== sectionId));
  }, []);

  const handleVideoSidebarToggle = () => {
    if (videoSidebarOpen) {
      setActiveSectionId(null);
      setVideoSidebarOpen(false);
      setIsPlaying(false);
    } else {
      if (!activeSectionId && registeredSections.length > 0) {
        const defaultSection = registeredSections.find(s => s.isDefault) || registeredSections[0];
        const defaultIdx = i18n.language === 'fr' ? 1 : 0;
        setActiveSectionId(defaultSection.sectionId);
        setCurrentVideoUrl(defaultSection.videos[defaultIdx]);
        setAllVideoUrls(defaultSection.videos);
        setVideoLabels(defaultSection.labels);
        setSelectedVideoIndex(defaultIdx);
      }
      setVideoSidebarOpen(true);
    }
  };

  const togglePlayPause = () => {
    if (currentVideoUrl && currentVideoUrl.includes('vimeo.com')) {
      if (iframeRef.current) {
        const method = isPlaying ? 'pause' : 'play';
        iframeRef.current.contentWindow.postMessage(JSON.stringify({ method }), '*');
      }
    } else {
      if (videoRef.current) {
        if (isPlaying) videoRef.current.pause();
        else videoRef.current.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  const navigateSection = (direction) => {
    if (registeredSections.length === 0) return;
    const currentIndex = registeredSections.findIndex(s => s.sectionId === activeSectionId);
    let newIndex;
    if (currentIndex === -1) {
      newIndex = 0;
    } else {
      newIndex = currentIndex + direction;
      if (newIndex < 0) newIndex = registeredSections.length - 1;
      if (newIndex >= registeredSections.length) newIndex = 0;
    }
    const section = registeredSections[newIndex];
    const defaultIdx = i18n.language === 'fr' ? 1 : 0;
    setActiveSectionId(section.sectionId);
    setCurrentVideoUrl(section.videos[defaultIdx]);
    setAllVideoUrls(section.videos);
    setVideoLabels(section.labels);
    setSelectedVideoIndex(defaultIdx);
    setIsPlaying(false);
    const el = section.getEl && section.getEl();
    // CSS scroll-margin-top on .tab-section-wrapper / .form-tab-overlay
    // provides the breathing room above the section.
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ----- Mobile swipe handlers on the sidebar peek/handle area -----
  // Swipe up on the bar = open. Swipe down = close. Only active on mobile
  // (the desktop sidebar slides horizontally, not vertically).
  const SWIPE_THRESHOLD_PX = 40;
  const HANDLE_AREA_PX = 110; // ~87px peek bar + tab protrusion

  const handleSidebarTouchStart = (e) => {
    tabClickSuppressRef.current = false;
    if (window.innerWidth > 1089 || !sidebarRef.current) return;
    const touch = e.touches[0];
    const rect = sidebarRef.current.getBoundingClientRect();
    const distanceFromTop = touch.clientY - rect.top;
    // Only register swipes that start in the top handle/peek area so the
    // content area below can still scroll normally when the sidebar is open.
    if (distanceFromTop > HANDLE_AREA_PX) {
      swipeRef.current.active = false;
      return;
    }
    swipeRef.current.startY = touch.clientY;
    swipeRef.current.currentY = touch.clientY;
    swipeRef.current.active = true;
  };

  const handleSidebarTouchMove = (e) => {
    if (!swipeRef.current.active) return;
    swipeRef.current.currentY = e.touches[0].clientY;
  };

  const handleSidebarTouchEnd = () => {
    if (!swipeRef.current.active) return;
    const delta = swipeRef.current.currentY - swipeRef.current.startY;
    swipeRef.current.active = false;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    // Suppress the click that fires after touchend if the swipe started on the tab
    tabClickSuppressRef.current = true;
    if (delta < 0 && !videoSidebarOpen) {
      handleVideoSidebarToggle();
    } else if (delta > 0 && videoSidebarOpen) {
      handleVideoSidebarToggle();
    }
  };

  const handleTabClick = () => {
    if (tabClickSuppressRef.current) {
      tabClickSuppressRef.current = false;
      return;
    }
    handleVideoSidebarToggle();
  };

  // ----- Desktop drag-to-resize on the sidebar's inner edge -----
  // Pointer capture keeps move/up events flowing to the handle even when the
  // cursor strays over the page (or the video iframe) mid-drag.
  // We drag the *visible* (effective) edge, then store the base width that
  // produces it (base = visible + nav offset). Operating on the visible width
  // keeps the drag 1:1 with no dead zone against the 320px floor when the nav
  // is open.
  const handleResizeStart = (e) => {
    e.preventDefault();
    const navOpen = sidebarOpen;
    resizeRef.current = {
      startX: e.clientX,
      startEffective: getEffectiveVideoWidth(videoSidebarWidth, navOpen),
      navOpen,
      active: true,
    };
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    document.documentElement.classList.add('video-sidebar-resizing');
  };

  const handleResizeMove = (e) => {
    if (!resizeRef.current.active) return;
    const { startX, startEffective, navOpen } = resizeRef.current;
    // Panel is docked to the right, so dragging left (clientX decreasing) widens it.
    const delta = startX - e.clientX;
    const targetEffective = clamp(
      startEffective + delta,
      VIDEO_SIDEBAR_MIN_WIDTH,
      getEffectiveVideoMaxWidth(navOpen)
    );
    setVideoSidebarWidth(targetEffective + (navOpen ? VIDEO_SIDEBAR_NAV_OFFSET : 0));
  };

  const handleResizeEnd = (e) => {
    if (!resizeRef.current.active) return;
    resizeRef.current.active = false;
    if (e.currentTarget.releasePointerCapture && e.pointerId != null) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* already released */ }
    }
    document.documentElement.classList.remove('video-sidebar-resizing');
  };

  // Keyboard resize (WAI-ARIA window-splitter pattern): arrows nudge, Home/End
  // jump. Operates on the visible width, same as the drag.
  const handleResizeKeyDown = (e) => {
    const navOpen = sidebarOpen;
    const maxEffective = getEffectiveVideoMaxWidth(navOpen);
    const currentEffective = getEffectiveVideoWidth(videoSidebarWidth, navOpen);
    let targetEffective;
    switch (e.key) {
      case 'ArrowLeft':  targetEffective = currentEffective + VIDEO_SIDEBAR_KEYBOARD_STEP; break; // wider
      case 'ArrowRight': targetEffective = currentEffective - VIDEO_SIDEBAR_KEYBOARD_STEP; break; // narrower
      case 'Home':       targetEffective = maxEffective; break;              // widest
      case 'End':        targetEffective = VIDEO_SIDEBAR_MIN_WIDTH; break;   // narrowest
      default: return;
    }
    e.preventDefault();
    const clamped = clamp(targetEffective, VIDEO_SIDEBAR_MIN_WIDTH, maxEffective);
    setVideoSidebarWidth(clamped + (navOpen ? VIDEO_SIDEBAR_NAV_OFFSET : 0));
  };

  return (
    <>
      <ScrollToTop />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} videoSidebarOpen={videoSidebarOpen} onVideoSidebarToggle={handleVideoSidebarToggle} />
      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`}>
        <Routes>
          {/* English routes */}
          <Route path="/" element={<LangWrapper lang="en"><Overview activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/forms" element={<LangWrapper lang="en"><Forms activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/forms/disability-tax-credit" element={<LangWrapper lang="en"><Form1 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/forms/canada-disability-benefit" element={<LangWrapper lang="en"><Form2 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          {/* French routes */}
          <Route path="/fr" element={<LangWrapper lang="fr"><Overview activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/formulaires" element={<LangWrapper lang="fr"><Forms activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/formulaires/credit-impot-pour-personnes-handicapees" element={<LangWrapper lang="fr"><Form1 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees" element={<LangWrapper lang="fr"><Form2 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
        </Routes>
      </main>
      
      {/* BACKDROP — mobile only, blocks page scroll/touch behind the sidebar */}
      {videoSidebarOpen && (
        <div className="video-sidebar-backdrop" onClick={handleVideoSidebarToggle} />
      )}

      {/* GLOBAL VIDEO SIDEBAR */}
      <aside
        ref={sidebarRef}
        className={`video-sidebar-global ${videoSidebarOpen ? 'open' : ''}`}
        onTouchStart={handleSidebarTouchStart}
        onTouchMove={handleSidebarTouchMove}
        onTouchEnd={handleSidebarTouchEnd}
      >
        {/* MOBILE HANDLE TAB — lives inside the sidebar so it always moves with it,
            keeping the tab perfectly aligned with the peek bar on every screen height */}
        <div className={`video-sidebar-tab ${videoSidebarOpen ? 'open' : ''}`} onClick={handleTabClick} title="Toggle video sidebar">
          <img src={asignIconwhite} alt="ASL Icon" className="video-sidebar-tab-icon" />
        </div>
        <div className={`video-sidebar-content ${videoSidebarOpen ? 'open' : ''}`}>
          {/* Language Toggle Tabs */}
          {videoLabels.length > 0 && (
            <div className="video-language-tabs">
              {videoLabels.map((label, index) => (
                <button
                  key={index}
                  className={`video-tab ${selectedVideoIndex === index ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedVideoIndex(index);
                    setCurrentVideoUrl(allVideoUrls[index]);
                    setIsPlaying(false);
                    const targetLang = index === 0 ? 'en' : 'fr';
                    if (i18n.language !== targetLang) {
                      const path = targetLang === 'fr'
                        ? (EN_TO_FR[location.pathname] || '/fr')
                        : (FR_TO_EN[location.pathname] || '/');
                      i18n.changeLanguage(targetLang);
                      navigate(path, { state: { preserveScroll: true } });
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="video-container"> 
            <div className="video-wrapper">
              {currentVideoUrl && currentVideoUrl.includes('vimeo.com') ? (
                <iframe
                  ref={iframeRef}
                  src={`https://player.vimeo.com/video/${currentVideoUrl.split('/').pop()}`}
                  className="video-player"
                  allowFullScreen
                  title="Vimeo Video"
                />
              ) : (
                <video
                  ref={videoRef}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  className="video-player"
                  src={currentVideoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            <div className="video-title">
              <p className="">{activeSectionId ? t(activeSectionId) : ''}</p>
            </div>

            <div className="video-player-controls">
              <div className="video-play-btn" onClick={togglePlayPause}>
                {isPlaying ? '⏸' : <img src={playIcon} alt="Play" className="video-control-icon" />}
              </div>
              <div className="video-next">
                <button onClick={() => navigateSection(-1)} className="video-nav-btn">
                  <img src={caretLeftIcon} alt="Previous" className="video-control-icon" />
                </button>
                <button onClick={() => navigateSection(1)} className="video-nav-btn">
                  <img src={caretRightIcon} alt="Next" className="video-control-icon" />
                </button>
              </div>
            </div>

            {/* Per-section transcript. Text is authored per section under the
                `transcripts` namespace and follows the active video's language
                (ASL ↔ EN, LSQ ↔ FR). Falls back to a placeholder until content
                is supplied. */}
            {activeSectionId && (() => {
              const transcriptKey = activeSectionId.replace(/^sections\./, 'transcripts.');
              const transcriptText = t(transcriptKey, { defaultValue: '' });
              return (
                <details className="video-transcript">
                  <summary className="video-transcript-summary">
                    <span>{t('transcript.label')}</span>
                    <span className="video-transcript-toggle" aria-hidden="true"></span>
                  </summary>
                  <div className="video-transcript-body">
                    <p dangerouslySetInnerHTML={{ __html: transcriptText || t('transcript.placeholder') }} />
                  </div>
                </details>
              );
            })()}

          </div>

            <p className={`asl-footer ${videoSidebarOpen ? 'open' : ''}`}>{t('translations.by')} <u><b><a href="https://asign.ca/" target="_blank" rel="noopener noreferrer">Asign</a></b></u></p>

        </div>
      </aside>

      {/* DESKTOP DRAG-TO-RESIZE HANDLE — sits on the open panel's inner edge.
          role="separator" + aria-value* make it operable by keyboard and
          announced by screen readers. Hidden on mobile via CSS. */}
      <div
        className={`video-sidebar-resizer ${videoSidebarOpen ? 'open' : ''}`}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('videoSidebar.resizeLabel', { defaultValue: 'Resize video panel' })}
        aria-valuemin={VIDEO_SIDEBAR_MIN_WIDTH}
        aria-valuemax={getEffectiveVideoMaxWidth(sidebarOpen)}
        aria-valuenow={getEffectiveVideoWidth(videoSidebarWidth, sidebarOpen)}
        tabIndex={videoSidebarOpen ? 0 : -1}
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
        onKeyDown={handleResizeKeyDown}
      >
        <span className="video-sidebar-resizer-grip" aria-hidden="true">
          <span className="video-sidebar-resizer-line" />
          <span className="video-sidebar-resizer-line" />
          <span className="video-sidebar-resizer-line" />
        </span>
      </div>

      <Footer
        activeSectionId={activeSectionId}
        setActiveSectionId={setActiveSectionId}
        setCurrentVideoUrl={setCurrentVideoUrl}
        setVideoSidebarOpen={setVideoSidebarOpen}
        setVideoLabels={setVideoLabels}
        setSelectedVideoIndex={setSelectedVideoIndex}
        setAllVideoUrls={setAllVideoUrls}
        registerSection={registerSection}
        unregisterSection={unregisterSection}
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
